/**
 * 건물 렌더링 전용 캔버스
 *
 * InteractiveBuildings, GUI 클리핑을 렌더링합니다.
 * 마우스 이벤트는 BackgroundCanvas에서 처리합니다.
 * z-index: 200으로 HTML 오버레이 위에 렌더링됩니다.
 */
import { useRef, useMemo, useCallback } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import * as THREE from "three";
import { IsometricCamera } from "@/three/cameras/IsometricCamera";
import { Lights } from "@/three/lights/Lights";
import { InteractiveBuildings } from "@/three/components/DisplayObjects";
import { useObliqueProjection } from "@/three/hooks/useObliqueProjection";
import { GRID_CONFIG } from "@/three/config/grid";
import { DEFAULT_PROJECTION_PARAMS } from "@/three/config/presets";
import { useCameraObservableStore } from "@/stores/cameraObservableStore";
import { useGUITriangleStore } from "@/stores/guiTriangleStore";
import type { PlacedObject } from "@/three/config/types";
import { EditorSlider } from "@/three/components/BuildingEditor/EditorSlider";
import { GUIClippingManager } from "@/three/components/GUIClippingManager";

// 에디터 박스 타입
interface EditorBoxData {
  id: string;
  position: [number, number, number];
  scale: [number, number, number];
}

interface BuildingCanvasProps {
  placedObjects: PlacedObject[];
  myCursor: { gridX: number; gridZ: number; color?: string } | null;
  hoveredBuildingId?: string | null;
  hideBuildings?: boolean;
  // 에디터 모드 props
  editorMode?: boolean;
  editorPosition?: { x: number; z: number };
  editorBoxes?: EditorBoxData[];
  selectedEditorBoxId?: string | null;
  selectedBuildingId?: string | null;
  // 입주 슬라이더 props
  tenantSliderMode?: boolean;
  tenantSliderBuilding?: {
    position: [number, number, number];
    width: number;
    depth: number;
    height: number;
  } | null;
  tenantAxis?: "x" | "z";
  tenantTextLength?: number;
  tenantPreviewU?: number;
  tenantPreviewV?: number;
  onTenantPreviewUChange?: (u: number) => void;
  onTenantPreviewVChange?: (v: number) => void;
  // GUI 클리핑 디버그 모드
  guiClippingDebug?: boolean;
  // 커서 스타일
  cursorNone?: boolean;
}

/**
 * Building Canvas 내부 씬 컨텐츠
 */
function BuildingSceneContent({
  placedObjects,
  myCursor,
  hoveredBuildingId = null,
  hideBuildings = false,
  editorMode = false,
  editorPosition,
  editorBoxes = [],
  selectedEditorBoxId = null,
  tenantSliderMode = false,
  tenantSliderBuilding = null,
  tenantAxis = "z",
  tenantTextLength = 0,
  tenantPreviewU = 0.5,
  tenantPreviewV = 0.5,
  onTenantPreviewUChange,
  onTenantPreviewVChange,
  selectedBuildingId = null,
  guiClippingDebug = false,
}: Omit<BuildingCanvasProps, "cursorNone">) {
  const sceneGroupRef = useRef<THREE.Group>(null);
  const projectionParams = DEFAULT_PROJECTION_PARAMS;
  const { camera } = useThree();

  // BackgroundCanvas의 cameraActions에서 getPanOffset 가져오기
  const getPanOffset = useCallback((): THREE.Vector3 => {
    const { cameraActions } = useCameraObservableStore.getState();
    if (cameraActions) {
      return cameraActions.getPanOffset();
    }
    return new THREE.Vector3();
  }, []);

  // 매 프레임마다 BackgroundCanvas의 줌과 동기화
  useFrame(() => {
    const { cameraActions } = useCameraObservableStore.getState();
    if (cameraActions && camera instanceof THREE.OrthographicCamera) {
      const zoom = cameraActions.getZoom();
      const expectedCameraZoom = 1 / zoom;
      // 줌이 다르면 업데이트
      if (Math.abs(camera.zoom - expectedCameraZoom) > 0.0001) {
        camera.zoom = expectedCameraZoom;
        camera.updateProjectionMatrix();
      }
    }
  });

  useObliqueProjection(sceneGroupRef, projectionParams, getPanOffset);

  const editorWorldPosition = useMemo(() => {
    if (!editorPosition) return null;
    return {
      x: editorPosition.x + GRID_CONFIG.CELL_SIZE / 2,
      z: editorPosition.z + GRID_CONFIG.CELL_SIZE / 2,
    };
  }, [editorPosition]);

  // 스토어에서 GUI 클리핑 설정 읽기
  const guiCorners = useGUITriangleStore((state) => state.corners);

  return (
    <>
      <GUIClippingManager
        enabled={true}
        projectionParams={projectionParams}
        getPanOffset={getPanOffset}
        debug={guiClippingDebug}
        topLeft={{
          enabled: guiCorners.topLeft.enabled,
          controlPoint: guiCorners.topLeft.controlPoint,
          hypotenuseOffset: guiCorners.topLeft.animationProgress,
        }}
        topRight={{
          enabled: guiCorners.topRight.enabled,
          controlPoint: guiCorners.topRight.controlPoint,
          hypotenuseOffset: guiCorners.topRight.animationProgress,
        }}
        bottomLeft={{
          enabled: guiCorners.bottomLeft.enabled,
          controlPoint: guiCorners.bottomLeft.controlPoint,
          hypotenuseOffset: guiCorners.bottomLeft.animationProgress,
        }}
        bottomRight={{
          enabled: guiCorners.bottomRight.enabled,
          controlPoint: guiCorners.bottomRight.controlPoint,
          hypotenuseOffset: guiCorners.bottomRight.animationProgress,
        }}
      />

      <IsometricCamera position={[30, 30, 30]} zoom={40} />
      <Lights />

      <group ref={sceneGroupRef}>
        {!hideBuildings && (
          <InteractiveBuildings
            buildings={placedObjects}
            hoveredBuildingId={editorMode ? null : hoveredBuildingId}
            myCursorColor={myCursor?.color || "#4ECDC4"}
            ghostMode={editorMode}
            selectedBuildingId={selectedBuildingId}
          />
        )}

        {tenantSliderMode &&
          tenantSliderBuilding &&
          (() => {
            const textHeight = tenantTextLength * 0.25;
            const textWidth = 0.3;
            const wallWidth =
              tenantAxis === "x"
                ? tenantSliderBuilding.depth
                : tenantSliderBuilding.width;
            const wallHeight = tenantSliderBuilding.height;
            const maxU = Math.max(0.1, 1 - textWidth / wallWidth);
            const maxV = Math.max(0.1, 1 - textHeight / wallHeight);
            const { width, depth, height } = tenantSliderBuilding;
            const halfWidth = width / 2;
            const halfDepth = depth / 2;

            const uSliderPos: [number, number, number] =
              tenantAxis === "x"
                ? [halfWidth, height, -halfDepth]
                : [-halfWidth, height, halfDepth];

            const vSliderPos: [number, number, number] =
              tenantAxis === "x"
                ? [halfWidth, 0, halfDepth]
                : [halfWidth, 0, halfDepth];

            const uAxis = tenantAxis === "x" ? "z" : "x";

            return (
              <group
                key={`tenant-sliders-${tenantAxis}`}
                position={tenantSliderBuilding.position}
              >
                <EditorSlider
                  axis={uAxis}
                  value={tenantPreviewU}
                  min={0}
                  max={maxU}
                  onChange={(v) => onTenantPreviewUChange?.(v)}
                  position={uSliderPos}
                  length={wallWidth}
                  color="#6b6bff"
                  markerColor="#3b82f6"
                  markerShape="cube"
                />
                <EditorSlider
                  axis="y"
                  value={tenantPreviewV}
                  min={0}
                  max={maxV}
                  onChange={(v) => onTenantPreviewVChange?.(v)}
                  position={vSliderPos}
                  length={height}
                  color="#6bff6b"
                  markerColor="#22c55e"
                  markerShape="cube"
                />
              </group>
            );
          })()}

        {editorMode && editorWorldPosition && (
          <group position={[editorWorldPosition.x, 0, editorWorldPosition.z]}>
            {editorBoxes.map((box) => (
              <mesh
                key={box.id}
                position={[
                  box.position[0] + box.scale[0] / 2,
                  box.position[1] + box.scale[1] / 2,
                  box.position[2] + box.scale[2] / 2,
                ]}
              >
                <boxGeometry
                  args={[box.scale[0], box.scale[1], box.scale[2]]}
                />
                <meshBasicMaterial
                  color={selectedEditorBoxId === box.id ? "#4ECDC4" : "#ffffff"}
                  transparent
                  opacity={0.9}
                />
                <lineSegments>
                  <edgesGeometry args={[new THREE.BoxGeometry(...box.scale)]} />
                  <lineBasicMaterial
                    color={
                      selectedEditorBoxId === box.id ? "#00aa88" : "#333333"
                    }
                  />
                </lineSegments>
              </mesh>
            ))}
          </group>
        )}
      </group>
    </>
  );
}

/**
 * Building Canvas 컴포넌트
 */
export function BuildingCanvas({
  cursorNone = false,
  ...props
}: BuildingCanvasProps) {
  return (
    <Canvas
      shadows
      gl={{ antialias: true, stencil: true, alpha: true }}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 200,
        pointerEvents: "none", // 오버레이가 마우스 이벤트 받도록
      }}
      className={cursorNone ? "cursor-none" : ""}
    >
      {/* R3F Canvas는 별도 React 트리를 사용하므로 QueryClientProvider를 다시 제공 */}
      <QueryClientProvider client={queryClient}>
        <BuildingSceneContent {...props} />
      </QueryClientProvider>
    </Canvas>
  );
}
